// Session summary screen — shown to parent after 15 exercises complete

import { useState, useEffect } from 'react';
import { getReport, downloadAuthenticatedJson, fetchAuthenticatedBlob, parentMilestoneUrl } from '../api/client';
import type { SessionReport, StudentProfile } from '../api/types';
import AdventurerStamp from '../components/adventurer/AdventurerStamp';
import type { AdventurerId } from '../components/adventurer/AdventurerStamp';

interface Props {
  profile: StudentProfile | null;
  adventurerId: AdventurerId;
  onPlayAgain: () => void;
  onLogout: () => void;
  onDashboard: () => void;
}

export default function SummaryScreen({ profile, adventurerId, onPlayAgain, onLogout, onDashboard }: Props) {
  const [report, setReport] = useState<SessionReport | null>(null);
  const [fresh, setFresh] = useState<StudentProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getReport()
      .then(({ report: r, profile: p }) => { setReport(r); setFresh(p); })
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, []);

  const accuracy = report
    ? Math.round((report.exercises_correct / Math.max(report.exercises_attempted, 1)) * 100)
    : null;

  // The report returns the profile after the session was recorded (chapter
  // advanced, streak and badges updated); the App-level profile is older.
  const story = (fresh ?? profile)?.story_state;
  const streak = story?.current_streak ?? 0;
  // Badges earned today (earned_at is local German time; sv-SE gives YYYY-MM-DD).
  const today = new Date().toLocaleDateString('sv-SE');
  const badgesToday = (story?.achievements ?? []).filter(a => a.earned_at.slice(0, 10) === today);

  // Chapter milestone: show a Teilen button for the most recently completed chapter.
  const chaptersCompleted = story?.chapters_completed ?? [];
  const latestChapterId = chaptersCompleted[chaptersCompleted.length - 1];

  // Focus-topic expiry nudge: focus_topics list is cleared when the decay
  // window hits 0, so an empty list + 0 remaining = "could set one again".
  const focusTopics = profile?.preferences?.focus_topics ?? [];
  const focusRemaining = profile?.preferences?.focus_topics_remaining ?? 0;
  const showFocusNudge = focusTopics.length === 0 && focusRemaining === 0;

  async function handleShareMilestone() {
    if (!latestChapterId) return;
    const url = parentMilestoneUrl(latestChapterId);
    try {
      const blob = await fetchAuthenticatedBlob(url);
      const file = new File([blob], `lernwelt-${latestChapterId}.png`, { type: 'image/png' });
      const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void>; canShare?: (d: ShareData) => boolean };
      if (nav.share && nav.canShare && nav.canShare({ files: [file] })) {
        await nav.share({ files: [file], title: 'Kapitel geschafft!', text: `${profile?.name ?? 'Mein Kind'} hat ein Kapitel abgeschlossen.` });
      } else {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objUrl);
      }
    } catch {
      window.open(url, '_blank');
    }
  }

  const labelStyle: React.CSSProperties = {
    fontFamily: 'var(--mono)',
    fontSize: 9, letterSpacing: '0.22em',
    color: 'var(--ink-muted)', textTransform: 'uppercase', marginBottom: 4,
  };

  return (
    <div style={{
      display: 'flex', height: '100dvh',
      alignItems: 'flex-start', justifyContent: 'center',
      background: 'var(--parchment)',
      fontFamily: 'var(--sans)',
      overflowY: 'auto',
      padding: '48px 24px 80px',
    }}>
      <div style={{ width: '100%', maxWidth: 540, display: 'flex', flexDirection: 'column', gap: 32 }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <AdventurerStamp id={adventurerId} size={72} />
          <div>
            <div style={{ fontFamily: 'var(--display)', fontSize: 36, fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Toll gemacht!
            </div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 16, fontStyle: 'italic', color: 'var(--ink-muted)', marginTop: 4 }}>
              {profile?.name ?? 'Dein Kind'} hat die heutige Runde abgeschlossen.
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ fontFamily: 'var(--serif)', fontSize: 18, fontStyle: 'italic', color: 'var(--ink-muted)' }}>
            Bericht wird erstellt…
          </div>
        ) : report ? (
          <>
            {/* stats */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
            }}>
              {[
                { label: 'Aufgaben', value: String(report.exercises_attempted) },
                { label: 'Richtig', value: String(report.exercises_correct) },
                { label: 'Genauigkeit', value: `${accuracy}%` },
              ].map(({ label, value }) => (
                <div key={label} style={{
                  background: 'var(--card)',
                  border: '1px solid rgba(60,50,40,0.1)',
                  borderRadius: 4,
                  padding: '16px 18px',
                  textAlign: 'center',
                }}>
                  <div style={labelStyle}>{label}</div>
                  <div style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 600, color: 'var(--ink)' }}>{value}</div>
                </div>
              ))}
            </div>

            {/* streak + badges earned today */}
            {(streak >= 2 || badgesToday.length > 0) && (
              <div style={{
                background: 'var(--amber-soft)',
                border: '1px solid rgba(201,106,47,0.3)',
                borderRadius: 4,
                padding: '16px 20px',
                display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                {streak >= 2 && (
                  <div style={{ fontFamily: 'var(--display)', fontSize: 20, color: 'var(--ink)' }}>
                    🔥 {streak} Tage in Folge dabei!
                  </div>
                )}
                {badgesToday.length > 0 && (
                  <div>
                    <div style={labelStyle}>Neue Auszeichnungen</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                      {badgesToday.map(a => (
                        <div key={a.achievement_id} title={a.description} style={{
                          padding: '6px 12px', background: 'var(--card)', borderRadius: 3,
                          fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--ink)',
                        }}>{a.icon} {a.label}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* summary text */}
            <div style={{
              background: 'var(--card)',
              border: '1px solid rgba(60,50,40,0.1)',
              borderRadius: 4,
              padding: '20px 24px',
            }}>
              <div style={labelStyle}>Zusammenfassung</div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 17, lineHeight: 1.6, color: 'var(--ink)' }}>
                {report.summary_text}
              </div>
            </div>

            {/* topic highlights */}
            {report.topic_highlights.length > 0 && (
              <div>
                <div style={{ ...labelStyle, marginBottom: 10 }}>Themen heute</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {report.topic_highlights.map((t, i) => (
                    <div key={i} style={{
                      padding: '10px 14px',
                      background: 'var(--card)',
                      border: '1px solid rgba(60,50,40,0.08)',
                      borderRadius: 3,
                      fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--ink)',
                      display: 'flex', alignItems: 'center', gap: 10,
                    }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--amber)', flexShrink: 0 }} />
                      {t}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* chapter milestone card — appears when a chapter has been completed */}
            {latestChapterId && (
              <div style={{
                padding: '16px 20px',
                background: 'var(--amber-soft)',
                border: '1px solid rgba(201,106,47,0.3)',
                borderRadius: 4,
                display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{ fontSize: 28 }}>🗺️</div>
                <div style={{ flex: 1 }}>
                  <div style={labelStyle}>Kapitel abgeschlossen</div>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: 14, color: 'var(--ink)', marginTop: 2 }}>
                    Teile diesen Moment als Bild — z. B. per WhatsApp oder E-Mail.
                  </div>
                </div>
                <button
                  onClick={handleShareMilestone}
                  style={{
                    padding: '10px 16px',
                    background: 'var(--amber)', color: '#fff', border: 'none',
                    borderRadius: 3, fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 700,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >Teilen</button>
              </div>
            )}

            {/* focus-topic nudge — shows after the parent's 20-exercise window expires */}
            {showFocusNudge && (
              <div style={{
                padding: '14px 18px',
                background: 'rgba(60,50,40,0.04)',
                border: '1px dashed rgba(60,50,40,0.18)',
                borderRadius: 4,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{ fontSize: 18, opacity: 0.7 }}>🎯</div>
                <div style={{ flex: 1, fontFamily: 'var(--serif)', fontSize: 13, fontStyle: 'italic', color: 'var(--ink-muted)', lineHeight: 1.5 }}>
                  Fokus erneut setzen? Wähle im Elternbereich bis zu 2 Themen — die nächsten 20 Aufgaben konzentrieren sich dann darauf.
                </div>
              </div>
            )}

            {/* suggested activity */}
            {report.suggested_home_activity && (
              <div style={{
                padding: '16px 20px',
                background: 'oklch(0.97 0.025 100)',
                border: '1px solid oklch(0.88 0.05 90)',
                borderRadius: 4,
              }}>
                <div style={labelStyle}>Übung für zu Hause</div>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 15, fontStyle: 'italic', color: 'var(--ink)', lineHeight: 1.55 }}>
                  {report.suggested_home_activity}
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ fontFamily: 'var(--serif)', fontSize: 16, fontStyle: 'italic', color: 'var(--ink-muted)' }}>
            Bericht konnte nicht geladen werden.
          </div>
        )}

        {/* actions */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={onDashboard}
            style={{
              padding: '14px 24px',
              background: 'var(--card)',
              color: 'var(--ink)', border: '1px solid rgba(60,50,40,0.2)',
              borderRadius: 3, fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
              cursor: 'pointer',
            }}
          >Lernfortschritt ansehen</button>
          <button
            onClick={onPlayAgain}
            style={{
              flex: 1, minWidth: 160, padding: '14px 24px',
              background: 'var(--amber)', color: '#fff', border: 'none',
              borderRadius: 3, fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 4px 12px rgba(201,106,47,0.3)',
            }}
          >Nochmal spielen →</button>

          <button
            onClick={() => downloadAuthenticatedJson(
              '/profile/export',
              `lernwelt-${profile?.name ?? 'profil'}.json`,
            ).catch(err => alert(err.message || 'Download fehlgeschlagen.'))}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '14px 20px',
              background: 'var(--card)',
              border: '1px solid rgba(60,50,40,0.2)',
              borderRadius: 3,
              fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
              color: 'var(--ink)', cursor: 'pointer',
            }}
          >Fortschritt speichern</button>

          <button
            onClick={onLogout}
            style={{
              padding: '14px 20px',
              background: 'none',
              border: '1px solid rgba(60,50,40,0.15)',
              borderRadius: 3, fontFamily: 'var(--sans)', fontSize: 14,
              color: 'var(--ink-muted)', cursor: 'pointer',
            }}
          >Abmelden</button>
        </div>
      </div>
    </div>
  );
}
